import React, { useState } from 'react';
import { Card, Table, Tag, Button, App, Input, Switch, Modal, Form, Select } from 'antd';
import { ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ownerIntelligenceService from '@/services/ownerIntelligence.service';
import { OiPageFrame, OiEmptyState } from '@/components/owner-intelligence/OwnerIntelligenceComponents';

const ExternalContextPage: React.FC = () => {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [form] = Form.useForm();
  const { data, isLoading, error } = useQuery({
    queryKey: ['owner-intelligence', 'external-context'],
    queryFn: () => ownerIntelligenceService.getExternalContext(),
  });

  const refreshAll = async () => {
    try {
      setRefreshing(true);
      await ownerIntelligenceService.refreshExternalContext();
      message.success('External signals refreshed');
      qc.invalidateQueries({ queryKey: ['owner-intelligence', 'external-context'] });
    } catch (e: unknown) {
      message.error((e as Error).message);
    } finally {
      setRefreshing(false);
    }
  };

  const toggleSource = async (record: Record<string, unknown>, enabled: boolean) => {
    try {
      await ownerIntelligenceService.upsertExternalSource({
        id: record.id,
        signal_type: record.signal_type,
        source_name: record.source_name,
        config_json: typeof record.config_json === 'string' ? JSON.parse(record.config_json) : record.config_json,
        is_enabled: enabled,
      });
      message.success(enabled ? 'Source enabled' : 'Source disabled');
      qc.invalidateQueries({ queryKey: ['owner-intelligence', 'external-context'] });
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const saveSource = async () => {
    try {
      const values = await form.validateFields();
      await ownerIntelligenceService.upsertExternalSource({
        signal_type: values.signal_type,
        source_name: values.source_name,
        config_json: { base_currency: values.base_currency || 'USD', quote_currency: values.quote_currency || 'AED' },
        is_enabled: true,
      });
      message.success('Source saved');
      setSourceOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['owner-intelligence', 'external-context'] });
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const signals = data?.signals ?? [];

  return (
    <OiPageFrame
      subtitle="FX, commodity, and macro signals — configured by administrators."
      loading={isLoading && !data}
      error={Boolean(error)}
      errorMessage="External context unavailable"
    >
      <Card
        title="External signals"
        bordered={false}
        extra={
          <>
            <Button icon={<PlusOutlined />} onClick={() => setSourceOpen(true)} style={{ marginRight: 8 }}>
              Add FX source
            </Button>
            <Button icon={<ReloadOutlined />} loading={refreshing} onClick={refreshAll}>
              Refresh enabled sources
            </Button>
          </>
        }
      >
        <Table
          size="small"
          rowKey="id"
          dataSource={signals}
          locale={{
            emptyText: (
              <OiEmptyState
                title="No external signals configured"
                body="Contact your administrator to add FX or commodity data sources, or use Add FX source if you have governance access."
                actionLabel="Add FX source"
                onAction={() => setSourceOpen(true)}
              />
            ),
          }}
          columns={[
            { title: 'Type', dataIndex: 'signal_type', render: (v: string) => <Tag>{v}</Tag> },
            { title: 'Source', dataIndex: 'source_name' },
            {
              title: 'Value',
              dataIndex: 'value',
              render: (v: unknown) => (typeof v === 'object' ? JSON.stringify(v) : String(v)),
            },
            { title: 'Unit', dataIndex: 'unit' },
            { title: 'Provider', dataIndex: 'source' },
            {
              title: 'Refreshed',
              dataIndex: 'refreshed_at',
              render: (v: string | null) => (v ? new Date(v).toLocaleString() : '—'),
            },
            {
              title: 'Enabled',
              dataIndex: 'enabled',
              render: (enabled: boolean, record: Record<string, unknown>) => (
                <Switch checked={enabled} onChange={(checked) => toggleSource(record, checked)} />
              ),
            },
          ]}
          pagination={false}
        />
        <p className="oi-subtitle" style={{ marginTop: 12, marginBottom: 0 }}>
          Last bundle refresh: {data?.refreshed_at ? new Date(data.refreshed_at).toLocaleString() : '—'}
        </p>
      </Card>

      <Modal title="Add FX source" open={sourceOpen} onCancel={() => setSourceOpen(false)} onOk={saveSource}>
        <Form form={form} layout="vertical" initialValues={{ signal_type: 'fx', base_currency: 'USD', quote_currency: 'AED' }}>
          <Form.Item name="signal_type" label="Type" rules={[{ required: true }]}>
            <Select options={[{ value: 'fx', label: 'FX rate' }, { value: 'commodity', label: 'Commodity (manual)' }]} />
          </Form.Item>
          <Form.Item name="source_name" label="Source name" rules={[{ required: true }]}>
            <Input placeholder="USD/AED Frankfurter" />
          </Form.Item>
          <Form.Item name="base_currency" label="Base currency">
            <Input />
          </Form.Item>
          <Form.Item name="quote_currency" label="Quote currency">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </OiPageFrame>
  );
};

export default ExternalContextPage;
